<script setup lang="ts">
  import { computed, useTemplateRef } from 'vue'
  import { useUser } from '@gat-ship/user'
  import { accessCodes } from '@gat-ship/types'
  import {
    Button,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription
  } from '@gat-ship/vue-components'
  import { IconPlus } from '@gat-ship/icons'
  import {
    CurrenciesList,
    CurrenciesModalProvider,
    CurrenciesProvider
  } from '@gat-ship/currencies'

  // The global AdministrationModal opens purely off `?admin=currencies` and is
  // mounted for every authenticated user, so this panel must gate itself — the
  // router CurrenciesView enforces the same check via PageLayout access-props.
  const user = useUser()

  // Add lives behind CurrenciesModalProvider; reach its showAddCurrency via the
  // provider's exposed API (the tenant-users/ports panel precedent). The panel
  // script runs before the providers mount, so it can't inject the collection —
  // it reads it through CurrenciesProvider's exposed dataSource instead, and
  // hides the header Add when the list is empty (CurrenciesList shows its own
  // empty-state Add then, so the two never collide).
  const modals =
    useTemplateRef<InstanceType<typeof CurrenciesModalProvider>>('modals')

  const currenciesProvider =
    useTemplateRef<InstanceType<typeof CurrenciesProvider>>(
      'currenciesProvider'
    )

  const isListEmpty = computed(
    () => currenciesProvider.value?.dataSource.currencies.value?.length === 0
  )

  function openAdd() {
    modals.value?.showAddCurrency({})
  }
</script>

<template>
  <div
    v-if="!user.hasPermission(accessCodes.system.currency.manageCurrencies)"
    class="text-muted-foreground p-10 text-center text-sm"
  >
    You don't have permission to manage currencies.
  </div>
  <CurrenciesProvider v-else ref="currenciesProvider">
    <CurrenciesModalProvider ref="modals">
      <!-- pt-3 offsets the modal's -mt-3 under-fade pull; pb-5 keeps the last
           row off the modal edge (the tenant-users/ports panel precedent). -->
      <div class="flex flex-col gap-6 pb-5 pt-3">
        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle
              >Currencies
              <Button
                v-if="!isListEmpty"
                class="ml-auto shrink-0"
                @click="openAdd"
              >
                <IconPlus class="size-4" />
                Add Currency
              </Button></ModalSectionHeaderTitle
            >
            <ModalSectionHeaderDescription>
              Manage the currencies that are available for use across the
              system. Control which currencies appear in currency lists. You can
              remove and re-add currencies at any time.
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <CurrenciesList />
        </ModalSection>
      </div>
    </CurrenciesModalProvider>
  </CurrenciesProvider>
</template>
